import { useEffect, useRef, type CSSProperties, type RefObject } from 'react';
import Share from 'components/Dashboard/components/Share';
import ContentSummary from 'components/Dashboard/components/ContentSummary';
import useTitleScrolledStyles from 'components/Dashboard/hooks/useTitleScrolledStyles';
import TextSelector from 'components/Common/TextSelector';
import useCreateMarkup from 'components/Dashboard/hooks/useCreateMarkup';
import time from 'utils/time';

interface GovernmentPublicationAttachment {
  title?: string;
  attachmentLink: string;
}

interface GovernmentPublication {
  id: number;
  objectId?: string;
  objectType?: string;
  title: string;
  titleHighlighted?: string;
  canonicalUrl?: string;
  publishedDate: string;
  sourceLink?: string;
  from?: string[];
  summary?: string;
  body?: string;
  attachments?: GovernmentPublicationAttachment[];
}

interface ShareButtonRenderProps {
  currentNew: GovernmentPublication;
  shareButtonWidget?: boolean;
  embed?: boolean;
}

interface GovernmentPublicationNewContentProps {
  currentNew: GovernmentPublication;
  contentContainer?: RefObject<HTMLElement | null>;
  hideSummary?: boolean;
  reportWidget?: boolean;
  scrollbarRef?: RefObject<unknown>;
  shareButtonWidget?: boolean;
  isFromPopup?: boolean;
  notIncludeBranding?: boolean;
  scrolling?: boolean;
  notShowShare?: boolean;
  embed?: boolean;
}

const ShareButtonRender = (props: ShareButtonRenderProps) => {
  const { currentNew, shareButtonWidget, embed } = props;
  const { id, title, canonicalUrl, objectId, objectType } = currentNew;
  const item = {
    id,
    title,
    objectID: objectId,
    type: objectType,
  };

  return (
    <Share
      item={item}
      id={id}
      title={title}
      shareButtonWidget={shareButtonWidget}
      itemShare={embed}
      link={canonicalUrl ? `${import.meta.env.VITE_APP_APPURL}${canonicalUrl}` : undefined}
    />
  );
};

const GovernmentPublicationNewContent = (props: GovernmentPublicationNewContentProps) => {
  const {
    currentNew,
    contentContainer,
    hideSummary,
    reportWidget,
    scrollbarRef,
    shareButtonWidget,
    isFromPopup,
    notIncludeBranding,
    scrolling,
    notShowShare,
    embed,
  } = props;
  const { title, titleHighlighted, publishedDate, sourceLink, from, summary, body, attachments } = currentNew;

  const titleSection = useRef(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const { showFixedTitle, titleStyles } = useTitleScrolledStyles({
    contentContainer,
    titleSection,
    leftValue: undefined,
  });
  const { createMarkup } = useCreateMarkup();

  useEffect(() => {
    bodyRef.current?.querySelectorAll('a').forEach((link) => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });
  }, [reportWidget, currentNew?.id]);

  const HeadingTitle = () => (
    <h3 className='main-title news-title pr-lg-4' dangerouslySetInnerHTML={createMarkup(titleHighlighted ?? title)} />
  );

  const shareButton = !notShowShare && !notIncludeBranding && (
    <ShareButtonRender currentNew={currentNew} shareButtonWidget={shareButtonWidget} embed={embed} />
  );

  return (
    <>
      <div
        className={`section-title pb-2 ${
          reportWidget ? 'content-title-in-widget' : 'content-title-with-summary'
        } content-border-title`}
        ref={titleSection}
      >
        <HeadingTitle />
        <div className='row mx-0 justify-content-between align-items-center pt-2'>
          <p className='heading-date mb-0'>
            <span className='date'>{time(publishedDate).formatLongDate()}</span>
            {!!from?.length && (
              <>
                <span className='dot' />
                {from.join(', ')}
              </>
            )}
            {!!sourceLink && (
              <>
                <span className='dot' />
                <a className='legislation-source mb-0' href={sourceLink} target='_blank' rel='noopener noreferrer'>
                  {reportWidget ? 'Source' : 'Go to source'}
                </a>
              </>
            )}
          </p>
          <div className='flex-centered'>{shareButton}</div>
        </div>
      </div>
      {!shareButtonWidget && <ContentSummary content={currentNew} hide={hideSummary} />}
      <div className='main-update-content-container government-publication-content'>
        <div className='px-3 px-lg-4 pb-3 pb-lg-4 specific-content-container'>
          <div
            className={`content-item-scrolled-title section-title bg-white ${
              showFixedTitle && !isFromPopup && !reportWidget ? 'item-scrolled-title-visible' : ''
            }`}
            style={titleStyles as CSSProperties | undefined}
          >
            <div className='d-flex justify-content-between scrolled-title-container'>
              <HeadingTitle />
              {shareButton}
            </div>
          </div>

          {!!summary && <p className='pt-2 mb-2 paragraph-p1 font-weight-bold'>{summary}</p>}

          <TextSelector currentNew={currentNew} scrollbarRef={scrollbarRef} scrolling={scrolling}>
            <div
              ref={bodyRef}
              className='news-content-in pt-2 pb-3 pr-3 context mb-2'
              dangerouslySetInnerHTML={createMarkup(body)}
            />

            {!!attachments?.length && (
              <div className='section-title pb-3 mb-3'>
                <h2 className='general-complementary-title mb-3'>Documents</h2>
                <ul className='policy-list px-0'>
                  {attachments.map(({ title: attachmentTitle, attachmentLink }, index) => (
                    <li className='mb-3' key={`attachment-${index}`}>
                      <a
                        href={attachmentLink}
                        target='_blank'
                        rel='noopener noreferrer'
                        download
                        className='attachment-box'
                      >
                        <span className='attachment-file-name'>{attachmentTitle ?? attachmentLink}</span>
                        <span aria-hidden='true' className='general-button'>
                          <span className='icon-download title-h5-m mx-3' />
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </TextSelector>
        </div>
      </div>
    </>
  );
};

export default GovernmentPublicationNewContent;
